/**
 * @file PickingPageHeader.tsx
 * @description Заголовок экрана «Задания на сборку» и вкладки «Очередь /
 * Журнал» (phase19.23, A1; phase19.31 шаг О4 — макет (г)).
 *
 * Заголовок — в верхней панели (`CabinetPageHeader`). Главного действия на
 * странице нет: задания создаются сами. «Обновить» — второстепенная кнопка
 * в панели на широком экране; на телефоне и планшете — «Обновить список» в
 * общей нижней полосе каркаса с подписью «Обновлено в 14:32» над ней (pull-
 * to-refresh в вебе нет). Выбор склада на узком экране — пункт «⋯».
 * Вкладки разделов — `Tabs` (линия снизу), а не пилюли-кнопки (SC45).
 *
 * Рисуется внутри панели режима: обновлять умеет только её хук с данными.
 */

import { RefreshCw } from "lucide-react";

import { PICKING_VIEW_LABEL, type PickingView } from "./picking-view";
import { Button } from "../../ui/button";
import { CabinetPageHeader } from "../../ui/CabinetPageHeader";
import type { PageAction } from "../../ui/page-actions";
import { Tabs } from "../../ui/Tabs";
import { BELOW_LG_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import { getShopTimeZone } from "../../../lib/shop-time";

/** Откуда берутся задания — пояснение под заголовком очереди. */
const QUEUE_HINT = "задание появляется, когда заказ переходит «На сборке»";
const HISTORY_HINT = "Проведённые и отменённые задания";

interface Props {
  view: PickingView;
  onViewChange: (view: PickingView) => void;
  onRefresh: () => void;
  loading: boolean;
  /** Последняя успешная загрузка — «Обновлено в 14:32». */
  loadedAt?: Date | null;
  /** Счётчик вкладки «Очередь» (известен, пока открыта очередь). */
  queueCount?: number;
  /** Пункт «⋯» на узком экране: выбор склада. */
  warehouseAction?: PageAction;
}

/** «14:32» по часовому поясу магазина (SC31). */
function timeText(date: Date): string {
  return date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit", timeZone: getShopTimeZone() });
}

export function PickingPageHeader({ view, onViewChange, onRefresh, loading, loadedAt, queueCount, warehouseAction }: Props) {
  const compact = useMediaQuery(BELOW_LG_QUERY);
  const updated = loadedAt ? `Обновлено в ${timeText(loadedAt)}` : null;
  const subtitle = view === "history" ? HISTORY_HINT : [updated, QUEUE_HINT].filter(Boolean).join(" · ");

  return (
    <>
      <CabinetPageHeader
        title="Задания на сборку"
        // На узком экране пояснение не занимает строку над вкладками (макет (г)).
        subtitle={compact ? undefined : subtitle}
        secondary={[{ key: "refresh", label: "Обновить", icon: RefreshCw, run: onRefresh, loading, compact: "hide" }]}
        menu={warehouseAction ? [warehouseAction] : []}
        menuLabel="Другие действия со сборкой"
        mobileAction={
          <Button variant="soft" onClick={onRefresh} loading={loading}>
            <RefreshCw className="size-4" />
            Обновить список
          </Button>
        }
        mobileNote={updated}
      />
      <Tabs
        aria-label="Режим списка заданий"
        items={[
          { value: "queue", label: PICKING_VIEW_LABEL.queue, count: view === "queue" ? queueCount : undefined },
          { value: "history", label: PICKING_VIEW_LABEL.history },
        ]}
        value={view}
        onChange={onViewChange}
      />
    </>
  );
}
